import React, { useState } from 'react';
import { clsx, colorSet } from '@components/Provider';
import AppConfig from '@config';
import { CapaQuotationChatFile } from '@generated/graphql';
import {
  CustomizedCircularProgress,
  CustomizedTypography
} from '@components/Customized';
import { makeStyles } from '@material-ui/core';
import useFiles from '@hooks/useFiles';

import { Image as ImageIcon } from '@carbon/icons-react';

import { Image } from '@components/Image';
import { formatFileSize } from '@utils/formatter';
import { shallowEqual } from 'react-redux';

import {
  getExtension,
  verifiableExtensions
} from '@components/Chat/ChatMenu/Viewer';
import { useSelector } from '@store';
import openViewer from '@utils/popupViewer';

interface Props {
  isOwner: boolean;
  chatFiles?: Array<CapaQuotationChatFile>;
}

const FileThumbnail: React.FC<Props> = ({ isOwner, chatFiles }) => {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const [overlay, setOverlay] = useState(false);
  const chatRoomId = useSelector((state) => state?.chat?.chatRoomId || '');
  const rfqId = useSelector((state) => state?.chat?.rfqId || '');

  const { onDownloadFile } = useFiles();

  const imageRegex = /(.*?)\.(jpg|png|jpeg)$/;

  const imageFile = chatFiles?.filter((file) => {
    if (file.fileName) {
      return (
        file.fileName && imageRegex.exec(file.fileName.toLocaleLowerCase())
      );
    } else {
      return false;
    }
  })?.[0];

  const files = chatFiles?.filter((file) => {
    if (file.fileName) {
      return (
        file.fileName && !imageRegex.exec(file.fileName.toLocaleLowerCase())
      );
    } else {
      return false;
    }
  })?.[0];

  const onFileDownload = () => {
    if (imageFile) {
      onDownloadFile(
        {
          fileId: imageFile?.fileId ?? '',
          fileName: imageFile?.fileName ?? ''
        },
        {
          onDownloadProgress: (e) => {
            if (imageFile.fileSize) {
              const percent = Math.round((e.loaded * 100) / imageFile.fileSize);

              setValue(percent);

              if (percent === 100) {
                setValue(0);
              }
            }
          }
        }
      );
    } else {
      onDownloadFile(
        {
          fileId: files?.fileId ?? '',
          fileName: files?.fileName ?? ''
        },
        {
          onDownloadProgress: (e) => {
            if (files?.fileSize) {
              const percent = Math.round((e.loaded * 100) / files.fileSize);

              setValue(percent);

              if (percent === 100) {
                setValue(0);
              }
            }
          }
        }
      );
    }
  };

  const onOpenViewer = () => {
    openViewer({
      chatRoomId,
      rfqId,
      selectedFileId: files?.fileId ?? ''
    });
  };

  const isVerfiedFile = verifiableExtensions.includes(
    getExtension(chatFiles?.[0].fileName as string)
  );

  return (
    <div
      className={clsx(classes.root, {
        default: !imageFile ? true : false
      })}
    >
      {imageFile && (
        <>
          {overlay && (
            <div
              className={classes.thumbnailOverlay}
              onMouseLeave={() => setOverlay(false)}
              onClick={() =>
                openViewer({
                  chatRoomId,
                  rfqId,
                  selectedFileId: imageFile.fileId ?? ''
                })
              }
            >
              <ImageIcon />
              뷰어로 보기
            </div>
          )}
          <Image
            onMouseEnter={() => setOverlay(true)}
            className={classes.thumbnailImage}
            alt="preview"
            src={`${AppConfig.fileApiUrl}/file/${imageFile?.fileId}`}
          />
          <div
            className={clsx(classes.buttons, {
              image: true
            })}
          >
            {isVerfiedFile && (
              <>
                <CustomizedTypography
                  className="clickable"
                  variant="body2"
                  color="textPrimary"
                  fontWeight="500"
                  onClick={() =>
                    openViewer({
                      chatRoomId,
                      rfqId,
                      selectedFileId: imageFile.fileId ?? ''
                    })
                  }
                >
                  뷰어로 보기
                </CustomizedTypography>

                <CustomizedTypography
                  className="dot"
                  variant="caption"
                  color="textSecondary"
                  fontWeight="500"
                >
                  ・
                </CustomizedTypography>
              </>
            )}

            <CustomizedTypography
              className="clickable"
              variant="body2"
              color="primary"
              fontWeight="500"
              onClick={onFileDownload}
            >
              다운로드
            </CustomizedTypography>
          </div>
        </>
      )}

      {!imageFile && (
        <>
          <div className={classes.fileName}>
            <CustomizedTypography
              className="file"
              variant="body1"
              color="textPrimary"
              fontWeight="500"
            >
              {files?.fileName}
            </CustomizedTypography>

            <CustomizedCircularProgress
              value={value}
              color="primary"
              variant="download"
              onDownload={onFileDownload}
            />
          </div>
          <CustomizedTypography
            variant="caption"
            color="textSecondary"
            gutterBottoms={16}
          >
            용량 : {formatFileSize(files?.fileSize ?? 0)}
          </CustomizedTypography>

          <div className={classes.buttons}>
            {isVerfiedFile && (
              <>
                <CustomizedTypography
                  className="clickable"
                  variant="body2"
                  color="textPrimary"
                  fontWeight="500"
                  onClick={onOpenViewer}
                >
                  뷰어로 보기
                </CustomizedTypography>

                <CustomizedTypography
                  className="dot"
                  variant="caption"
                  color="textSecondary"
                  fontWeight="500"
                >
                  ・
                </CustomizedTypography>
              </>
            )}
            <CustomizedTypography
              className="clickable"
              variant="body2"
              color="primary"
              onClick={onFileDownload}
              fontWeight="500"
            >
              다운로드
            </CustomizedTypography>
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(FileThumbnail, (prev, curr) => {
  return (
    shallowEqual(prev.chatFiles, curr.chatFiles) &&
    prev.isOwner === curr.isOwner
  );
});

const useStyles = makeStyles((theme) => {
  return {
    root: {
      position: 'relative',
      width: 230,
      '&.default': {
        padding: 16,
        backgroundColor: colorSet.gray000,
        borderRadius: 10
      }
    },
    thumbnailImage: {
      width: '100%',
      borderRadius: '10px 10px 0px 0px'
    },
    thumbnailOverlay: {
      cursor: 'pointer',
      position: 'absolute',
      top: 0,
      backgroundColor: `rgba(0,0,0,0.3)`,
      width: '100%',
      height: 'calc(100% - 52px)',
      borderRadius: `10px 10px 0px 0px`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': {
        width: 24,
        height: 24,
        marginBottom: 2,
        color: colorSet.gray000
      },
      ...theme.typography.subtitle2,
      color: colorSet.gray000
    },
    button: {
      border: 'none',
      borderRadius: '0px 0px 10px 10px',
      backgroundColor: colorSet.gray000
    },
    startIcon: {
      '& img': {
        width: 26,
        height: 26
      }
    },
    buttonLabel: {
      justifyContent: 'space-between'
    },
    fileName: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 4,
      '& .file': {
        width: 158,
        wordBreak: 'break-all'
      }
    },
    buttons: {
      display: 'flex',
      alignItems: 'center',
      '& .dot': {
        margin: '0px 4px'
      },
      '& .clickable': {
        cursor: 'pointer'
      },
      '&.image': {
        padding: 16,
        backgroundColor: colorSet.gray000,
        borderRadius: '0px 0px 10px 10px'
      }
    }
  };
});
